// 📦 Import des dépendances React et hooks
import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useLocation } from "react-router-dom";

// 📦 Import des composants Material UI utilisés pour la mise en page et l'UI
import { Paper, useMediaQuery } from "@mui/material";

import FormulaireGroupe from "../components/FormulaireGroupe.js"; // 👈 direct
import HeaderGroupe from "../components/HeaderGroupe.js";
import DialogSuppressionGroupe from "../components/DialogSuppressionGroupe.js";
import ContactTable from "../components/ContactTable.js";
import QuotaInfo from "../components/QuotaInfo.js";

import useGroupeValidation from "../hooks/useGroupeValidation.js";

import SnackbarFeedback from "./SnackbarFeedback.js";

// 🌐 Contexte de langue pour la traduction
import { useLanguage } from '../context/LanguageContext.js';
import { useGroupe } from "../context/GroupeContext.js";

import { logDev } from "../utils/logger.js";

const Contacts = () => {
  
  const { t, lang} = useLanguage();      
  const isMobile = useMediaQuery("(max-width:600px)");
  
  // --------------------
  // États liés au groupe
  // --------------------
  const [groupeCree, setGroupeCree] = useState(false);
  const [nomGroupe, setNomGroupe] = useState("");
  const [emailCreateur, setEmailCreateur] = useState("");
  const [telCreateur, setTelCreateur] = useState("");
  const [motdepasseAdmin, setMotdepasseAdmin] = useState("");          
  const [motdepasseUtilisateur, setMotdepasseUtilisateur] = useState(""); 
  const [editionNomActive, setEditionNomActive] = useState(false);
  const [nouveauNomGroupe, setNouveauNomGroupe] = useState("");
  const [nouvelEmoji, setNouvelEmoji] = useState("");
  const [nouvelleCouleur, setNouvelleCouleur] = useState("#71685e");
  const [createur, setCreateur] = useState("");
  const [emailConnect, setEmailConnect] = useState("");
  const [nomGroupeConnecte, setNomGroupeConnecte] = useState("");
  const [groupeActif, setGroupeActif] = useState(null);
  const [contacts, setContacts] = useState([]);
  const { groupeId, nom, emoji, couleur} = useGroupe();
  useGroupeValidation(groupeId);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);

  // --------------------
  // Gestion du rôle
  // --------------------
  const [role, setRole] = useState(null);
  const isAdmin = role === "admin";
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const upgrade = params.get("upgrade");
    const duration = params.get("duration");

    if (upgrade === "success") {
      const message = duration
        ? `${t("GroupExtendedSuccessfully")} (+${duration} mois)`
        : t("GroupExtendedSuccessfully");

      setSnackbar({
        open: true,
        severity: "success",
        message,
        persistent: true
      });
    }
  }, [location.search]);

  useEffect(() => {
    const header = document.querySelector('.header');
    const footer = document.querySelector('.footer');

    const updateHeights = () => {
      if (header) setHeaderHeight(header.offsetHeight);
      if (footer) setFooterHeight(footer.offsetHeight);
    };

    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  const topOffset = headerHeight + (isMobile ? footerHeight : bottomNavHeight);
  const bottomOffset = isMobile ? bottomNavHeight : footerHeight;

  // Récupération du rôle stocké en sessionStorage
  useEffect(() => {
    const storedRole = sessionStorage.getItem("role");
    if (storedRole) {
      setRole(storedRole);
    }
  }, []);

  const onContactsChange = (updatedContacts, isAdmin) => {
  // Tu peux logguer, synchroniser, ou déclencher autre chose ici
  logDev("[Contacts] Mise à jour des contacts", {
    total: updatedContacts.length,
    admin: isAdmin
  });
  };

  useEffect(() => {
    if (onContactsChange) {
      onContactsChange(contacts, isAdmin);
    }
  }, [contacts, isAdmin, onContactsChange]);

  // --------------------
  // États pour UI
  // --------------------
  const [openConfirm, setOpenConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Calcul dynamique de la hauteur de la bottom navigation
  useEffect(() => {
    const nav = document.querySelector('.MuiBottomNavigation-root');
    if (nav) {
      const updateHeight = () => setBottomNavHeight(nav.offsetHeight);
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, []);

  // Récupération de l'email connecté depuis le sessionstorage
  useEffect(() => {
    const email = sessionStorage.getItem("emailConnect");
    if (email) setEmailConnect(email);
  }, []);

// 1️⃣ Charger les infos du groupe actif
useEffect(() => {
  if (!groupeId) return;

  setGroupeCree(true);

  axios
    .get(`${process.env.REACT_APP_API}/groupes/${groupeId}`)
    .then(res => {
      setGroupeActif(res.data);
      setNomGroupeConnecte(res.data.nom);
      setCreateur(res.data.createur);
      sessionStorage.setItem("emailConnect", res.data.createur);
      setEmailConnect(res.data.createur);
    })
    .catch(err => {
      logDev("❌ Erreur récupération groupe", { message: err.message, stack: err.stack });
      sessionStorage.removeItem("groupeId");
      setGroupeCree(false);
      setGroupeActif(null);
    });
}, [groupeId]); // ← FIN DU PREMIER useEffect

// 2️⃣ Charger les contacts du groupe
useEffect(() => {
  if (!groupeId) return;

  axios
    .get(`${process.env.REACT_APP_API}/contacts?groupeId=${groupeId}`)
    .then(res => {
      setContacts(res.data);
    })
    .catch(err => {
      logDev("❌ Erreur récupération contacts", { message: err.message, stack: err.stack });
    });
}, [groupeId]); 

  // --------------------
  // Suppression du groupe
  // --------------------
  const handleDeleteGroup = async () => {
    logDev("📡 Envoi DELETE", {
      url: `${process.env.REACT_APP_API}/groupes/${groupeId}`,
      utilisateurId: emailConnect
    });

    try {
      logDev("🧪 Vérification emailConnect", { emailConnect });
      if (!groupeActif?._id) return;
      await axios.delete(`${process.env.REACT_APP_API}/groupes/${groupeActif._id}`, {
        data: { utilisateurId: emailConnect, lang }
      });
      
      logDev("🔤 Traduction snackbar", { message: t('GroupDeleted') });

      setSnackbar({ open: true, message: t('GroupDeleted'), severity: "success" });
      sessionStorage.removeItem("groupeId");
      setTimeout(() => window.location.href = "/", 1500);
    
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || err.message;

  if (status === 403) {
    logDev("⛔ Suppression refusée", { status, message });
    setSnackbar({ open: true, message, severity: "warning" });
  } else {
    logDev("❌ Erreur suppression groupe", { status, message });
    setSnackbar({ open: true, message: t('DeleteGroupFailed') , severity: "error" });
  }
}
};

  if (!groupeId) return <Navigate to="/connexion" replace />;

  const reloadContacts = async () => {
  const res = await axios.get(`${process.env.REACT_APP_API}/contacts?groupeId=${groupeId}`);
  setContacts(res.data);
};

  // --------------------
  // Rendu JSX
  // --------------------
  return (
    <div 
    className="contacts-container"
    style={{
      minHeight: "100vh",
      paddingTop: `${topOffset}px`,
      paddingBottom: `${bottomOffset}px`,
      boxSizing: "border-box",
      backgroundColor: "#f4ece2",
      overflowY: "auto"
    }}
    
    >
    {isAdmin && groupeActif?._id && (
    <QuotaInfo groupeId={groupeActif._id} isAdmin={isAdmin} />
    )}
      <div >
        {!groupeCree && (
          <FormulaireGroupe
            nomGroupe={nomGroupe}
            setNomGroupe={setNomGroupe}
            emailCreateur={emailCreateur}
            setEmailCreateur={setEmailCreateur}
            telCreateur={telCreateur}
            setTelCreateur={setTelCreateur}
            motdepasseAdmin={motdepasseAdmin}                 
            setMotdepasseAdmin={setMotdepasseAdmin}           
            motdepasseUtilisateur={motdepasseUtilisateur}     
            setMotdepasseUtilisateur={setMotdepasseUtilisateur} 
            setGroupeCree={setGroupeCree}
            setGroupeActif={setGroupeActif}
            setNomGroupeConnecte={setNomGroupeConnecte}
            sondageVerrouille={groupeActif?.sondageVerrouille} 
          />
        )}

        {/* Si un groupe existe, on affiche ses infos */}
        {groupeCree && nomGroupeConnecte && (
          <Paper
            elevation={1}
            sx={{
              backgroundColor: "#f4ece2",
              width: "90vw",
              padding: 2,
            }}
          >
            {/* 🔔 Notice rôle : message différent pour admin ou utilisateur */}
            <div
              style={{
                backgroundColor: "#2f2f2fff",
                padding: "12px 16px",
                borderRadius: "6px",
                marginBottom: "12px",
                fontSize: "1rem",
                fontWeight: "bold",
                textAlign: "center",
                color: "#fff",
                border: "2px solid #d4af37", // doré
                boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                backgroundImage: "radial-gradient(circle at top left, rgba(255,255,255,0.05), transparent 70%)"
              }}
            >
              {isAdmin
                ? t('ContactAdmin') 
                : t("ContactUsers")}
            </div>

            {/* Nom du groupe + actions admin */}
            <HeaderGroupe
              nomGroupeConnecte={nomGroupeConnecte}
              emoji={groupeActif?.emoji}
              nouvelEmoji={nouvelEmoji}
              setNouvelEmoji={setNouvelEmoji}
              couleur={groupeActif?.couleur}
              nouvelleCouleur={nouvelleCouleur}
              setNouvelleCouleur={setNouvelleCouleur}
              isAdmin={isAdmin}
              isMobile={isMobile}
              bottomNavHeight={bottomNavHeight}
              headerHeight={headerHeight}
              editionNomActive={editionNomActive}
              setEditionNomActive={setEditionNomActive}
              nouveauNomGroupe={nouveauNomGroupe}
              setNouveauNomGroupe={setNouveauNomGroupe}
              groupeActif={groupeActif}
              setGroupeActif={setGroupeActif}
              setContacts={setContacts}
              reloadContacts={reloadContacts}
              emailConnect={emailConnect}
              setNomGroupeConnecte={setNomGroupeConnecte}
              setOpenConfirm={setOpenConfirm}
              setSnackbar={setSnackbar}
              t={t}
              sondageVerrouille={groupeActif?.sondageVerrouille} 
            />

            {/* Tableau des contacts */}
            <ContactTable
              t={t}
              contacts={contacts}
              groupeCree={groupeCree}
              groupeId={groupeActif?._id}
              nomGroupe={groupeActif?.nom}
              createur={groupeActif?.createur}
              emailConnect={emailConnect}
              role={role}
              setContacts={setContacts} // 👈 tu passes le setter
              onContactsUpdated={(updated) => setContacts(updated)}
              snackbar={snackbar}           // ✅ Ajouté
              setSnackbar={setSnackbar}     // ✅ Ajouté
              sondageVerrouille={groupeActif?.sondageVerrouille} 
            />
          </Paper>
        )}
      </div>

      {/* Dialog de confirmation de suppression*/}
      <DialogSuppressionGroupe
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleDeleteGroup}
        t={t}
      />

      {/* Snackbar pour afficher les messages de succès/erreur */}
      <SnackbarFeedback 
        snackbar={snackbar} 
        setSnackbar={setSnackbar}
        bottomNavHeight={bottomNavHeight}
        emoji={emoji}
        couleur={couleur}
       />
    </div>
  );
};

export default Contacts;
